/**
 * AppOnboardingTour — guided first-time walkthrough for new users.
 *
 * A multi-step modal walkthrough of the product as it is shipped:
 * 1. Welcome to FTL — what the platform does
 * 2. Home — the Command Center: what needs your decision now
 * 3. Felix — your AI program manager (Ask Felix in the sidebar)
 * 4. The Iterator — talk an idea into a design (only when the org has it)
 * 5. Command Palette — Cmd+K for instant search
 * 6. Getting Started — create your first project
 *
 * Row 10aa2f23 ("tour rewritten"): the previous copy described a
 * drag-and-drop widget dashboard that no longer exists and never mentioned
 * the Iterator. Each step's `highlight` names the mounted control the step
 * tells the user to use; AppOnboardingTour.test.tsx checks every one
 * resolves in the rendered sidebar.
 *
 * localStorage persistence — only shows once per user.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence, m } from "framer-motion";
import {
  Sparkles, Bot, LayoutDashboard, Command, MessagesSquare,
  Rocket, ChevronRight, ChevronLeft, X, PartyPopper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useModalA11y, dialogA11yProps } from "@/hooks/useModalA11y";
import { useV3Flag, V3_FLAGS } from "@/lib/featureFlagsV3";

const STORAGE_KEY = "ftl-app-onboarding-seen";

function hasSeen(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === "true"; } catch { return false; }
}

function markSeen(): void {
  try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
}

export function resetAppOnboarding(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

// ── Tour steps ─────────────────────────────────────────────────────

interface TourStep {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  detail: string;
  /** CSS selector of the mounted control this step tells the user to use
   *  (FelixRail). Not a spotlight: the tour is a centred modal. The test
   *  renders the rail and checks each selector resolves. */
  highlight?: string;
  /** Shown only when the org can open the surface. The Iterator step uses the
   *  same gate as the rail and the palette (ftl.ux.v3.iterator_mvp), so the
   *  tour never points someone at an entry their sidebar does not have. */
  requiresIterator?: boolean;
}

// Exported for AppOnboardingTour.test.tsx (the highlight-target check).
// eslint-disable-next-line react-refresh/only-export-components
export const TOUR_STEPS: TourStep[] = [
  {
    icon: Sparkles,
    iconColor: "text-brand-500",
    iconBg: "bg-brand-500/10",
    title: "Welcome to FTL",
    description: "The AI-Powered Software Development Lifecycle Platform",
    detail: "FTL runs your delivery from first idea to shipped work. Felix, your AI program manager, drives the routine work and brings you only the decisions that need a person.",
  },
  {
    icon: LayoutDashboard,
    iconColor: "text-blue-500",
    iconBg: "bg-blue-500/10",
    title: "Home Is Your Command Center",
    description: "It opens on what needs your decision now",
    detail: "Home leads with what needs you today — decisions waiting on you, work that is blocked, what is due — with Felix's briefing on the rest. Anything that moved on its own stays out of your way. Home in the sidebar always brings you back here.",
    highlight: 'nav[aria-label="Primary navigation"] a[href="/"]',
  },
  {
    icon: Bot,
    iconColor: "text-purple-500",
    iconBg: "bg-purple-500/10",
    title: "Meet Felix — Your AI Program Manager",
    description: "Felix isn't a chatbot. He runs the work.",
    // Felix's intelligence lives on the surfaces (Home, briefings, status
    // explanations); chat is for anything the surfaces don't cover.
    detail: "Felix works in the background and surfaces what needs you on Home. When you want something done, click Ask Felix at the top of the sidebar and say it in plain English: create a project, break down requirements, draft a document, check for conflicts, run QA.",
    highlight: 'button[aria-label="Ask Felix"]',
  },
  {
    icon: MessagesSquare,
    iconColor: "text-emerald-500",
    iconBg: "bg-emerald-500/10",
    title: "Build It in the Iterator",
    description: "Talk an idea into a design with Felix",
    detail: "Open a project and choose Iterator under Execute. Describe what you want; Felix drafts it on the canvas and you mark it up until it is right. What you settle on becomes real project work: requirements, tasks, risks and decisions.",
    highlight: 'nav[aria-label="Primary navigation"] a[href$="/iterator"]',
    requiresIterator: true,
  },
  {
    icon: Command,
    iconColor: "text-amber-500",
    iconBg: "bg-amber-500/10",
    title: "Command Palette",
    description: "Press ⌘K (Ctrl+K) to find anything instantly",
    detail: "Search for projects, entities, team members, or actions — all from one keyboard shortcut. It's the fastest way to navigate FTL. Press ? to see every keyboard shortcut.",
  },
  {
    icon: Rocket,
    iconColor: "text-rose-500",
    iconBg: "bg-rose-500/10",
    title: "Ready to Launch",
    description: "Start your first project or talk to Felix",
    detail: "You're all set. Start a new project and pick how you want to work it — Iterate with Felix, or plan it step by step — or simply tell Felix what you're building and he'll set it up with you.",
  },
];

// ── Step indicator ──────────────────────────────────────────────────

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={cn(
            "h-1.5 rounded-full transition-all duration-300",
            i === current ? "w-6 bg-brand-500" : i < current ? "w-1.5 bg-brand-300" : "w-1.5 bg-text-tertiary/20",
          )}
        />
      ))}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────

export function AppOnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  // The Iterator step is offered only where the Iterator opens (same gate as
  // the rail and the command palette).
  const iterator = useV3Flag(V3_FLAGS.ITERATOR_MVP);
  const steps = useMemo(
    () => TOUR_STEPS.filter((s) => !s.requiresIterator || iterator.enabled),
    [iterator.enabled],
  );

  useEffect(() => {
    // Show after a short delay (let the app render first). Never over a
    // meeting (register row 38): the lobby renders inside the AppShell, and
    // "seen" is per browser, so a member opening Tuesday's link on a fresh
    // browser got the tour over the lobby's Join button. It shows on their
    // next ordinary page instead.
    if (!hasSeen() && !window.location.pathname.startsWith("/meeting/")) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // And an open tour steps aside when the member goes into a meeting (bob-agent's
  // review of #4261): the AppShell stays mounted across route changes, so the
  // mount-time check above would leave a tour opened on another page over the
  // lobby. Polled, not router-bound: this component is also rendered without a
  // router. It stays unseen, so it shows again on the next full page load.
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      if (window.location.pathname.startsWith("/meeting/")) setOpen(false);
    }, 500);
    return () => clearInterval(id);
  }, [open]);

  // Bug fix 2026-05-04: persist "seen" state on the FIRST step
  // interaction, not only on dismiss/completion. Pre-fix users who
  // saw the tour, glanced at it, then navigated to a different page
  // (without clicking X or finishing) had the tour re-appear on
  // every subsequent page load — markSeen() was only called from
  // handleDismiss, never from handleNext / handlePrev. Now the
  // tour records "seen" the moment the user advances or rewinds
  // the first step, AND when the component unmounts while open
  // (route change). Either signal is enough to confirm the user
  // engaged with the tour.
  useEffect(() => {
    if (open && step > 0) {
      markSeen();
    }
  }, [open, step]);

  useEffect(() => {
    // On unmount while the tour is still open (e.g. route change
    // pulled the AppShell from under us), record seen so we don't
    // show again on the next page render.
    return () => {
      if (open) markSeen();
    };
  }, [open]);

  const handleDismiss = useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);

  const handleNext = useCallback(() => {
    // Mark seen as soon as the user advances the first time —
    // doesn't wait for completion.
    markSeen();
    if (step < steps.length - 1) {
      setStep((s) => s + 1);
    } else {
      handleDismiss();
    }
  }, [step, steps.length, handleDismiss]);

  const handlePrev = useCallback(() => {
    markSeen();
    setStep((s) => Math.max(0, s - 1));
  }, []);

  // Escape + focus-trap + focus-restore + scroll-lock for the tour modal.
  const dialogRef = useModalA11y<HTMLDivElement>({ open, onClose: handleDismiss });

  const current = steps[Math.min(step, steps.length - 1)];
  const Icon = current.icon;
  const isLast = step >= steps.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // Holistic-review fix: lighter first-run overlay. Dropped the heavy
          // bg-black/70 + backdrop-blur (which read as a hard wall the moment a
          // new user landed) to a softer scrim so the app stays visible behind
          // the welcome card. Still a focused modal (dismissible via X / Skip /
          // Esc), just not an opaque block.
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45"
        >
          <m.div
            ref={dialogRef}
            {...dialogA11yProps({ label: "Product tour" })}
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative w-full max-w-lg mx-4 overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-800 shadow-2xl"
          >
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 z-10 p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Close tour"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Brand header — solid brand-600 for Modernize consistency. */}
            <div className="relative overflow-hidden bg-brand-600 px-8 pt-8 pb-6">
              <m.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4"
              />
              <m.div
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4"
              />

              <AnimatePresence mode="wait">
                <m.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="relative"
                >
                  <m.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.1 }}
                    className={cn("inline-flex p-3 rounded-2xl mb-4", current.iconBg)}
                  >
                    <Icon className={cn("h-7 w-7", current.iconColor)} />
                  </m.div>
                  <h2 className="text-xl font-bold text-white">{current.title}</h2>
                  <p className="text-sm text-white/70 mt-1">{current.description}</p>
                </m.div>
              </AnimatePresence>

              {/* Step counter */}
              <div className="absolute top-4 left-8">
                <span className="text-[10px] font-medium text-white/50">
                  {step + 1} / {steps.length}
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="px-8 py-6">
              <AnimatePresence mode="wait">
                <m.p
                  key={step}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed"
                >
                  {current.detail}
                </m.p>
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-8 pb-6">
              <StepDots current={step} total={steps.length} />
              <div className="flex items-center gap-2">
                {step > 0 && (
                  <Button variant="ghost" size="sm" onClick={handlePrev} className="gap-1">
                    <ChevronLeft className="h-3.5 w-3.5" /> Back
                  </Button>
                )}
                {!isLast && (
                  // Persistent escape on every step (was step 0 only), so the
                  // tour never feels like a wall the user must click through.
                  <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-slate-500 dark:text-slate-400">
                    Skip tour
                  </Button>
                )}
                <Button size="sm" onClick={handleNext} className="gap-1">
                  {isLast ? (
                    <>
                      <PartyPopper className="h-3.5 w-3.5" />
                      Let's Go!
                    </>
                  ) : (
                    <>
                      Next <ChevronRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
